import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import z from "zod";

import { useGet } from "../../../api-client/useGet";
import { useForm } from "../../../hooks/useForm";
import { useBatchGetVehicleManufacturers } from "../../../queries/useBatchGetVehicleManufacturers";
import { VehicleModel } from "../../../queries/useGetVehicleModels";
import { VehicleSubmodel } from "../../../queries/useGetVehicleSubmodels";
import { useGetVehicleYears } from "../../../queries/useGetVehicleYears";
import { useNationalAccountId } from "../../../stores/hooks/useNationalAccountId";
import { useNullableDealerAccountInfo } from "../../../stores/hooks/useNullableDealerAccountInfo";
import { VehicleManufacturerEntry } from "../../../stores/slices/packageEditorSlice";
import { Checkbox } from "../../../ui/Checkbox/Checkbox";
import { LinearProgress } from "../../../ui/LinearProgress";
import { MenuButton } from "../../../ui/Menu/MenuButton";

const wellClassNames =
  "border border-edge rounded-section bg-input overflow-auto";

const stringToArraySchema = (
  message: string
): z.ZodEffects<z.ZodTypeAny, string[], unknown> =>
  z.preprocess(
    (val) => (Array.isArray(val) ? val : [val]),
    z.array(z.string()).nonempty({ message })
  );

const schema = z.object({
  vehicleManufacturerIds: stringToArraySchema(
    "form.error.vehicleManufacturerIds"
  ),
  vehicleYearIds: stringToArraySchema("form.error.vehicleYearIds"),
});

export const vehicleLoaderFormId = "vehicle-loader";

export const VehicleLoader: FunctionComponent<{
  setModelsAreLoading(modelsAreLoading: boolean): void;
  setVehicleManufacturers(
    vehicleManufacturers: VehicleManufacturerEntry[]
  ): void;
  toggleVehicleManufacturerId(manufacturerId: number): void;
  toggleVehicleYearId(yearId: number): void;
  vehicleManufacturerIds: number[];
  vehicleYearIds: number[];
}> = ({
  setModelsAreLoading,
  setVehicleManufacturers,
  toggleVehicleManufacturerId,
  toggleVehicleYearId,
  vehicleManufacturerIds,
  vehicleYearIds,
}) => {
  const { t } = useTranslation();
  const { get } = useGet();
  const dealer = useNullableDealerAccountInfo();
  const nationalAccountId = useNationalAccountId();

  const { data: vehicleYears, isLoading } = useGetVehicleYears();
  const { data: vehicleManufacturers } =
    useBatchGetVehicleManufacturers(vehicleYearIds);

  const [handleForm] = useForm({
    async onSubmit({ vehicleManufacturerIds, vehicleYearIds }) {
      setModelsAreLoading(true);
      try {
        const vehicleManufacturerEntries: VehicleManufacturerEntry[] = [];
        for (const vehicleYearId of vehicleYearIds) {
          for (const vehicleManufacturerId of vehicleManufacturerIds) {
            const { payload: models } = await get<VehicleModel[]>(
              "/retailSiteWebService/vehicle/getVehicleModels.php",
              {
                dealerId: dealer?.dealerId || "",
                nationalAccountId,
                vehicleManufacturerId: String(vehicleManufacturerId),
                vehicleYearId: String(vehicleYearId),
              }
            );
            for (const {
              vehicleModelClassId,
              vehicleModelId,
              vehicleModelName,
            } of models) {
              const { payload: submodels } = await get<VehicleSubmodel[]>(
                `/retailSiteWebService/vehicle/getVehicleSubmodels.php`,
                {
                  dealerId: dealer?.dealerId || "",
                  nationalAccountId,
                  vehicleCategoryId: vehicleModelClassId,
                  vehicleManufacturerId: String(vehicleManufacturerId),
                  vehicleModelId: String(vehicleModelId),
                  vehicleYearId: String(vehicleYearId),
                }
              );

              for (const {
                vehicleSubmodelId,
                vehicleSubmodelImage,
                vehicleSubmodelName,
                vehicleSubmodelTiresizeArray,
              } of submodels) {
                const vehicleManufacturerName = vehicleManufacturers!.find(
                  (man) => man.vehicleManufacturerId == vehicleManufacturerId
                )?.vehicleManufacturerName as string;

                const vehicleYear = vehicleYears!.find(
                  (year) => year.value == vehicleYearId
                )?.label as string;

                vehicleManufacturerEntries.push({
                  vehicleManufacturerName,
                  vehicleModelId,
                  vehicleModelName,
                  vehicleSubmodelId,
                  vehicleSubmodelImage,
                  vehicleSubmodelName,
                  vehicleSubmodelTiresizeArray,
                  vehicleYear,
                  vehicleYearId,
                });
              }
            }
          }
        }
        setVehicleManufacturers(vehicleManufacturerEntries);
      } finally {
        setModelsAreLoading(false);
      }
    },
    schema,
  });

  if (isLoading) {
    return <LinearProgress className="mx-4 h-0.5" />;
  }

  return (
    <div className="my-2">
      <h4 className="mx-4 my-2">{t("packageEditor.selectVehicleYears")}</h4>
      <form
        className={clsx(
          "grid gap-3 mx-4",
          !!vehicleYearIds.length && "md:grid-cols-2"
        )}
        id={vehicleLoaderFormId}
        noValidate
        onSubmit={handleForm}
      >
        <div className={wellClassNames}>
          {vehicleYears?.map((option) => (
            <MenuButton
              aria-label="selectYear"
              element="label"
              key={option.value}
            >
              <Checkbox
                onChange={(): void => {
                  toggleVehicleYearId(option.value);
                }}
                checked={vehicleYearIds.includes(option.value)}
                name="vehicleYearIds"
                value={option.value}
              />
              {option.label}
            </MenuButton>
          ))}
        </div>
        {!!vehicleYearIds.length && (
          <div className={wellClassNames}>
            {vehicleManufacturers?.map(
              ({ vehicleManufacturerId, vehicleManufacturerName }) => (
                <MenuButton
                  aria-label="selectYear"
                  element="label"
                  key={vehicleManufacturerId}
                >
                  <Checkbox
                    checked={vehicleManufacturerIds.includes(
                      vehicleManufacturerId
                    )}
                    onChange={(): void => {
                      toggleVehicleManufacturerId(vehicleManufacturerId);
                    }}
                    name="vehicleManufacturerIds"
                    value={vehicleManufacturerId}
                  />
                  {vehicleManufacturerName}
                </MenuButton>
              )
            )}
          </div>
        )}
      </form>
    </div>
  );
};
